import { useQuery } from '@tanstack/react-query';
import { TicketAPI } from '@base/api/ticket-api';
import { queryKeys } from './queries';

const ticketAPI = new TicketAPI();

/**
 * Fetches ticket list by external incident ID.
 * Note: API returns a single ticket object; wrapping in array for list compatibility.
 */
export function useTicketList(incidentId: string) {
  return useQuery({
    queryKey: queryKeys.tickets(incidentId),
    queryFn: async () => {
      try {
        const ticket = await ticketAPI.getByExternalId(incidentId);

        // API may return HTTP 200 with data: null when no ticket exists.
        // In that case, BaseRepository returns the raw response body
        // (e.g. { data: null, message: "..." }) instead of a valid ticket.
        // Validate response content to detect "not found" scenario.
        if (!ticket || typeof ticket !== 'object' || !('name' in ticket) || !ticket.name) {
          return [];
        }

        return [ticket];
      } catch (err: unknown) {
        // API may also return 404 when no ticket exists — treat as empty list.
        // The response interceptor normalizes AxiosError into AppError { code, message, details },
        // so we check AppError.code instead of AxiosError.response.status.
        if (
          err &&
          typeof err === 'object' &&
          'code' in err &&
          (err as { code?: string }).code === 'NOT_FOUND'
        ) {
          return [];
        }
        throw err;
      }
    },
    staleTime: 0,
    enabled: !!incidentId,
  });
}

/**
 * Fetches a single ticket detail by external ID.
 * Uses GET /api/method/field-ticket?ticket_id_external=xxx
 */
export function useTicketDetail(externalId: string) {
  return useQuery({
    queryKey: queryKeys.ticket(externalId),
    queryFn: () => ticketAPI.getByExternalId(externalId),
    staleTime: 30_000, // 30s – prevents refetch right after setQueryData from mutation
    enabled: !!externalId,
  });
}
